import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Zap, FileText, MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AIInsights() {
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [report, setReport] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const runPredictive = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/predictive-maintenance`, {}, { withCredentials: true });
      setPrediction(r.data);
    } catch { toast.error('AI request failed'); }
    setLoading(false);
  };

  const runReport = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/generate-report`, {}, { withCredentials: true });
      setReport(r.data);
    } catch { toast.error('AI request failed'); }
    setLoading(false);
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', text: chatInput };
    setChat((prev) => [...prev, userMsg]);
    const prompt = chatInput;
    setChatInput('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/chat`, { prompt, session_id: sessionId }, { withCredentials: true });
      setSessionId(r.data.session_id);
      setChat((prev) => [...prev, { role: 'assistant', text: r.data.reply }]);
    } catch { toast.error('AI request failed'); setChat((prev) => [...prev, { role: 'assistant', text: 'Error: Could not reach AI service.' }]); }
    setLoading(false);
  };

  return (
    <Layout>
      <div data-testid="ai-insights-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-teal-500" />AI Insights
          </h1>
          <p className="text-sm text-muted-foreground">Powered by Claude Sonnet — predictive maintenance, complaint triage, and executive reports</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Predictive Maintenance */}
          <Card className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg"><Zap className="w-5 h-5 text-amber-700" /></div>
                <h2 className="font-semibold text-foreground">Predictive Maintenance</h2>
              </div>
              <Button data-testid="run-predictive-btn" onClick={runPredictive} disabled={loading} size="sm" className="bg-teal-600 hover:bg-teal-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analyze'}
              </Button>
            </div>
            {prediction ? (
              <div className="space-y-2">
                <div className="flex gap-4 text-xs text-slate-600 mb-2">
                  <span>📦 {prediction.analyzed_assets} assets</span>
                  <span>⏰ {prediction.overdue_tasks} overdue</span>
                </div>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 bg-slate-50 p-3 rounded-lg max-h-80 overflow-y-auto">{prediction.insights}</div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Click Analyze to get AI-powered predictions about asset failures and preventive actions.</p>
            )}
          </Card>

          {/* Executive Report */}
          <Card className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg"><FileText className="w-5 h-5 text-blue-700" /></div>
                <h2 className="font-semibold text-foreground">Executive Report</h2>
              </div>
              <Button data-testid="run-report-btn" onClick={runReport} disabled={loading} size="sm" className="bg-teal-600 hover:bg-teal-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
              </Button>
            </div>
            {report ? (
              <div className="space-y-2">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 bg-slate-50 p-3 rounded-lg max-h-80 overflow-y-auto">{report.report}</div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Generate an AI-written executive summary for your CEO dashboard.</p>
            )}
          </Card>
        </div>

        {/* Chat */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg"><MessageSquare className="w-5 h-5 text-purple-700" /></div>
            <h2 className="font-semibold text-foreground">FM Chat Assistant</h2>
          </div>
          <div data-testid="ai-chat-window" className="space-y-3 max-h-96 overflow-y-auto mb-3 p-2">
            {chat.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">Ask anything about your facilities, PPM, complaints, SLAs…</p>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.role === 'user' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-900'}`}>
                  <p className="whitespace-pre-wrap">{m.text}</p>
                </div>
              </div>
            ))}
            {loading && chat.length > 0 && chat[chat.length - 1].role === 'user' && (
              <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-4 py-2"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div></div>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              data-testid="ai-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Ask: Which buildings have most complaints?"
              rows={2}
              className="flex-1"
            />
            <Button data-testid="send-chat-btn" onClick={sendChat} disabled={!chatInput.trim() || loading} className="bg-teal-600 hover:bg-teal-700">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
